import React, { useState, useEffect } from 'react';
import SpecInput from './components/SpecInput';
import DocViewer from './components/DocViewer';
import { parseSpec } from './utils/parseSpec';

/* ── shadcn/ui token helpers (pure CSS vars, no hardcoded colors) ── */
const t = {
  header: {
    position: 'sticky', top: 0, zIndex: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', height: '56px',
    backgroundColor: 'var(--background)',
    borderBottom: '1px solid var(--border)',
    backdropFilter: 'blur(8px)',
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  brandIcon: {
    width: '28px', height: '28px', borderRadius: 'calc(var(--radius) * 0.8)',
    background: 'var(--primary)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0,
  },
  brandName: {
    fontFamily: 'Geist, sans-serif', fontWeight: 600, fontSize: '15px',
    letterSpacing: '-0.02em', color: 'var(--foreground)',
  },
  versionBadge: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '2px 7px', borderRadius: 'calc(var(--radius) * 0.6)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--muted-foreground)',
    letterSpacing: '0.02em',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  specActiveBadge: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '3px 9px', borderRadius: 'calc(var(--radius) * 0.6)',
    border: '1px solid var(--border)',
    background: 'var(--muted)', color: 'var(--muted-foreground)',
    maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  themeBtn: {
    display: 'flex', alignItems: 'center', gap: '5px',
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '5px 10px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--secondary-foreground)',
    cursor: 'pointer', letterSpacing: '0.02em',
    transition: 'background 0.15s, color 0.15s',
  },
  main: {
    maxWidth: '840px', width: '100%', margin: '0 auto',
    padding: '48px 24px 96px',
  },
  hero: {
    marginBottom: '40px', paddingBottom: '36px',
    borderBottom: '1px solid var(--border)',
  },
  heroEyebrow: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)', marginBottom: '12px',
  },
  heroH1: {
    fontFamily: 'Geist, sans-serif', fontWeight: 700,
    fontSize: 'clamp(28px, 4vw, 42px)',
    letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '12px',
    color: 'var(--foreground)',
  },
  heroAccent: { color: 'var(--muted-foreground)' },
  heroParagraph: {
    fontSize: '14px', color: 'var(--muted-foreground)',
    maxWidth: '520px', lineHeight: 1.75,
  },
  footer: {
    borderTop: '1px solid var(--border)',
    padding: '18px 24px', textAlign: 'center',
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    color: 'var(--muted-foreground)', letterSpacing: '0.03em',
  },
};

function SunIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

/* small code/slash SVG brand icon — represents API docs */
function BrandIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 5L1 8l3 3" stroke="var(--primary-foreground)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 5l3 3-3 3" stroke="var(--primary-foreground)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.5 3l-3 10" stroke="var(--primary-foreground)" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

export default function App() {
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('apiscribe-theme');
      if (saved) return saved === 'dark';
    } catch (_) {}
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  /* keep <html> class in sync */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    try { localStorage.setItem('apiscribe-theme', isDark ? 'dark' : 'light'); } catch (_) {}
  }, [isDark]);

  const handleParse = (input) => {
    setError(null);
    try {
      setSpec(parseSpec(input));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ── */}
      <header style={t.header}>
        <div style={t.brandRow}>
          <div style={t.brandIcon}>
            <BrandIcon />
          </div>
          <span style={t.brandName}>APIScribe</span>
          <span style={t.versionBadge}>v1.0.0</span>
        </div>

        <div style={t.headerRight}>
          {spec && (
            <span style={t.specActiveBadge} title={spec.info.title}>
              {spec.info.title}
            </span>
          )}
          <button
            style={t.themeBtn}
            onClick={() => setIsDark(d => !d)}
            aria-label="Toggle theme"
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
            {isDark ? 'Light' : 'Dark'}
          </button>
        </div>
      </header>

      {/* ── Main ── */}
      <main style={t.main}>
        {!spec ? (
          <>
            <div style={t.hero}>
              <p style={t.heroEyebrow}>App K · Phase 2 · Weeks 22–25</p>
              <h1 style={t.heroH1}>
                Beautiful API docs.<br />
                <span style={t.heroAccent}>From any OpenAPI spec.</span>
              </h1>
              <p style={t.heroParagraph}>
                Paste or upload any OpenAPI 3.x or Swagger 2.0 JSON/YAML.
                APIScribe renders interactive documentation with live Try-It panels
                and exports a fully standalone HTML file. Free alternative to Swagger UI
                and Readme.com.
              </p>
            </div>
            <SpecInput onParse={handleParse} error={error} />
          </>
        ) : (
          <DocViewer
            spec={spec}
            onReset={() => { setSpec(null); setError(null); }}
          />
        )}
      </main>

      {/* ── Footer ── */}
      <footer style={t.footer}>
        APIScribe — documentation for teams that care about craft · React · OpenAPI · Prism.js
      </footer>
    </div>
  );
}
